import { LitElement, html, css } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

const SUGGESTED_TAGS = [
  'route',
  'alternate',
  'campsite',
  'waypoint',
  'poi',
  'boundary',
  'trailhead',
  'water',
  'hazard',
  'parking',
]

@customElement('tag-editor-dropdown')
export class TagEditorDropdown extends LitElement {
  static override styles = css`
    :host {
      position: absolute;
      z-index: 2000;
    }

    .dropdown {
      background: rgba(30, 41, 59, 0.95);
      backdrop-filter: blur(12px);
      border-radius: 12px;
      padding: 12px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
      width: 264px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .section-label {
      font-size: 11px;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.6);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .tags-container {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }

    .tag-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 4px 8px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 500;
      background: rgba(59, 130, 246, 0.3);
      color: #93c5fd;
      border: 1px solid rgba(59, 130, 246, 0.4);
    }

    .tag-chip .remove {
      cursor: pointer;
      font-size: 14px;
      line-height: 1;
      opacity: 0.7;
      transition: opacity 0.15s ease;
      font-family: 'Material Symbols Outlined';
    }

    .tag-chip .remove:hover {
      opacity: 1;
    }

    .input-row {
      display: flex;
      gap: 6px;
    }

    .tag-input {
      flex: 1;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      padding: 6px 10px;
      color: white;
      font-size: 13px;
      outline: none;
      min-width: 0;
    }

    .tag-input::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }

    .tag-input:focus {
      border-color: rgba(59, 130, 246, 0.6);
    }

    .suggestions {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
    }

    .suggestion {
      padding: 3px 8px;
      border-radius: 10px;
      font-size: 11px;
      background: rgba(255, 255, 255, 0.08);
      color: rgba(255, 255, 255, 0.6);
      border: 1px solid rgba(255, 255, 255, 0.12);
      cursor: pointer;
      transition: all 0.15s ease;
    }

    .suggestion:hover {
      background: rgba(255, 255, 255, 0.15);
      color: rgba(255, 255, 255, 0.9);
    }

    .empty-state {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.4);
      font-style: italic;
    }
  `

  @property({ type: Array })
  tags: string[] = []

  @state()
  private inputValue = ''

  private addTag(tag: string): void {
    const normalized = tag.trim().toLowerCase()
    if (!normalized || this.tags.includes(normalized)) return

    const newTags = [...this.tags, normalized]
    this.dispatchEvent(
      new CustomEvent('tags-changed', {
        detail: { tags: newTags },
        bubbles: true,
      })
    )
    this.inputValue = ''
  }

  private removeTag(tag: string): void {
    const newTags = this.tags.filter(t => t !== tag)
    this.dispatchEvent(
      new CustomEvent('tags-changed', {
        detail: { tags: newTags },
        bubbles: true,
      })
    )
  }

  private handleKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      e.preventDefault()
      this.addTag(this.inputValue)
    } else if (e.key === 'Backspace' && !this.inputValue && this.tags.length > 0) {
      this.removeTag(this.tags[this.tags.length - 1])
    }
  }

  private handleInput(e: Event): void {
    this.inputValue = (e.target as HTMLInputElement).value
  }

  override render(): unknown {
    const unusedSuggestions = SUGGESTED_TAGS.filter(s => !this.tags.includes(s))

    return html`
      <div class="dropdown">
        <div class="section-label">Tags</div>

        ${this.tags.length > 0
          ? html`
              <div class="tags-container">
                ${this.tags.map(
                  tag => html`
                    <span class="tag-chip">
                      ${tag}
                      <span class="remove" @click=${() => this.removeTag(tag)}>close</span>
                    </span>
                  `
                )}
              </div>
            `
          : html`<div class="empty-state">No tags yet</div>`}

        <div class="input-row">
          <input
            class="tag-input"
            type="text"
            placeholder="Add a tag..."
            .value=${this.inputValue}
            @input=${this.handleInput}
            @keydown=${this.handleKeydown}
          />
        </div>

        ${unusedSuggestions.length > 0
          ? html`
              <div class="section-label">Suggestions</div>
              <div class="suggestions">
                ${unusedSuggestions.map(
                  s => html`
                    <button class="suggestion" @click=${() => this.addTag(s)}>${s}</button>
                  `
                )}
              </div>
            `
          : ''}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tag-editor-dropdown': TagEditorDropdown
  }
}
